import { useState } from 'react';
import { Plus, Edit2, Trash2, Save, Building2 } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useApp } from '../context/AppContext';

const BALANCE_TYPES = ['Asset', 'Liability', 'Equity'];
const COLORS = ['#10B981', '#EF4444', '#3B82F6'];

const formatNumber = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

const BalanceSheetForm = ({ onSubmit, onClose, initialData }) => {
  const [formData, setFormData] = useState(initialData || {
    type: 'Asset',
    name: '',
    amount: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ ...formData, amount: parseFloat(formData.amount) });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
        <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
          {BALANCE_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          placeholder="e.g., Cash & Bank, Equipment" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
        <input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required placeholder="0.00" />
      </div>
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
        <button type="submit" className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center justify-center gap-2">
          <Save className="w-4 h-4" /> Save
        </button>
      </div>
    </form>
  );
};

const BalanceSheetPage = () => {
  const { balanceSheetList, addBalanceSheet, updateBalanceSheet, deleteBalanceSheet, loading } = useApp();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const handleAdd = async (data) => {
    await addBalanceSheet(data);
    setModalOpen(false);
  };

  const handleEdit = async (data) => {
    await updateBalanceSheet(editingItem.id, data);
    setEditingItem(null);
    setModalOpen(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this item?')) {
      await deleteBalanceSheet(id);
    }
  };

  // Group by type
  const assets = balanceSheetList.filter(item => item.type === 'Asset');
  const liabilities = balanceSheetList.filter(item => item.type === 'Liability');
  const equity = balanceSheetList.filter(item => item.type === 'Equity');

  const totalAssets = assets.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalLiabilities = liabilities.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalEquity = equity.reduce((sum, item) => sum + (item.amount || 0), 0);

  const pieData = [
    { name: 'Assets', value: totalAssets },
    { name: 'Liabilities', value: totalLiabilities },
    { name: 'Equity', value: totalEquity }
  ].filter(item => item.value > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Balance Sheet</h1>
          <p className="text-gray-500 text-sm">Assets, liabilities, and equity</p>
        </div>
        <button onClick={() => { setEditingItem(null); setModalOpen(true); }}
          className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Item
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm border-l-4 border-l-emerald-500">
          <p className="text-gray-500 text-sm mb-1">Total Assets</p>
          <p className="text-2xl font-bold text-emerald-600">{formatNumber(totalAssets)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm border-l-4 border-l-red-500">
          <p className="text-gray-500 text-sm mb-1">Total Liabilities</p>
          <p className="text-2xl font-bold text-red-600">{formatNumber(totalLiabilities)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm border-l-4 border-l-blue-500">
          <p className="text-gray-500 text-sm mb-1">Total Equity</p>
          <p className="text-2xl font-bold text-blue-600">{formatNumber(totalEquity)}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-medium mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-red-700" /> Balance Distribution
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value">
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatNumber(value)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-2">
            {pieData.map((item, index) => (
              <div key={item.name} className="flex items-center gap-1 text-xs">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }} />
                <span className="text-gray-600">{item.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Equation */}
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-medium mb-4">Accounting Equation</h3>
          <div className="space-y-4">
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-emerald-700 text-sm">Assets</p>
              <p className="text-2xl font-bold text-emerald-600">{formatNumber(totalAssets)}</p>
            </div>
            <div className="text-center text-gray-400 text-xl">=</div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-red-700 text-sm">Liabilities</p>
              <p className="text-2xl font-bold text-red-600">{formatNumber(totalLiabilities)}</p>
            </div>
            <div className="text-center text-gray-400 text-xl">+</div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-blue-700 text-sm">Equity</p>
              <p className="text-2xl font-bold text-blue-600">{formatNumber(totalEquity)}</p>
            </div>
            {totalAssets !== totalLiabilities + totalEquity && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-amber-700 text-sm">
                Note: Balance sheet is not balanced. Please review entries.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Balance Sheet Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-red-700 to-red-800">
          <h3 className="font-semibold text-white">Balance Sheet Items</h3>
        </div>
        <div className="p-4">
          {/* Assets Section */}
          <div className="mb-6">
            <h4 className="font-semibold text-emerald-600 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full" /> Assets
            </h4>
            {assets.length > 0 ? (
              <table className="w-full text-sm">
                <tbody>
                  {assets.map((item) => (
                    <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2 text-gray-700">{item.name}</td>
                      <td className="py-2 text-right text-gray-800 font-medium">{formatNumber(item.amount)}</td>
                      <td className="py-2 text-right w-20">
                        <button onClick={() => { setEditingItem(item); setModalOpen(true); }}
                          className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-700">
                          <Edit2 className="w-3 h-3" />
                        </button>
                        <button onClick={() => handleDelete(item.id)}
                          className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-600">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-emerald-50">
                    <td className="py-2 font-bold text-emerald-700">Total Assets</td>
                    <td className="py-2 text-right font-bold text-emerald-700">{formatNumber(totalAssets)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <p className="text-gray-500 text-sm py-2">No assets recorded</p>
            )}
          </div>

          {/* Liabilities Section */}
          <div className="mb-6">
            <h4 className="font-semibold text-red-600 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full" /> Liabilities
            </h4>
            {liabilities.length > 0 ? (
              <table className="w-full text-sm">
                <tbody>
                  {liabilities.map((item) => (
                    <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2 text-gray-700">{item.name}</td>
                      <td className="py-2 text-right text-gray-800 font-medium">{formatNumber(item.amount)}</td>
                      <td className="py-2 text-right w-20">
                        <button onClick={() => { setEditingItem(item); setModalOpen(true); }}
                          className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-700">
                          <Edit2 className="w-3 h-3" />
                        </button>
                        <button onClick={() => handleDelete(item.id)}
                          className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-600">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-red-50">
                    <td className="py-2 font-bold text-red-700">Total Liabilities</td>
                    <td className="py-2 text-right font-bold text-red-700">{formatNumber(totalLiabilities)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <p className="text-gray-500 text-sm py-2">No liabilities recorded</p>
            )}
          </div>

          {/* Equity Section */}
          <div>
            <h4 className="font-semibold text-blue-600 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full" /> Equity
            </h4>
            {equity.length > 0 ? (
              <table className="w-full text-sm">
                <tbody>
                  {equity.map((item) => (
                    <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2 text-gray-700">{item.name}</td>
                      <td className="py-2 text-right text-gray-800 font-medium">{formatNumber(item.amount)}</td>
                      <td className="py-2 text-right w-20">
                        <button onClick={() => { setEditingItem(item); setModalOpen(true); }}
                          className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-700">
                          <Edit2 className="w-3 h-3" />
                        </button>
                        <button onClick={() => handleDelete(item.id)}
                          className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-600">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-blue-50">
                    <td className="py-2 font-bold text-blue-700">Total Equity</td>
                    <td className="py-2 text-right font-bold text-blue-700">{formatNumber(totalEquity)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <p className="text-gray-500 text-sm py-2">No equity recorded</p>
            )}
          </div>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => { setModalOpen(false); setEditingItem(null); }} title={editingItem ? 'Edit Item' : 'Add Balance Sheet Item'}>
        <BalanceSheetForm onSubmit={editingItem ? handleEdit : handleAdd} onClose={() => { setModalOpen(false); setEditingItem(null); }} initialData={editingItem} />
      </Modal>
    </div>
  );
};

export default BalanceSheetPage;
